<div class="video-player-container" [class.full-window]="isFullWindowMode">
  <!-- Loading Indicator -->
  <div *ngIf="isLoading" class="loading-overlay">
    <div class="loading-spinner"></div>
    <p>Loading media...</p>
  </div>
  <!-- Error Message -->
  <div *ngIf="error" class="error-message">
    <p>⚠️ {{ error }}</p>
    <button (click)="clearError()" class="error-close">×</button>
  </div>
  <!-- Mobile Debug Panel -->
  <div *ngIf="showMobileDebugPanel" class="mobile-debug-panel" [class.expanded]="showMobileDebug">
    <div class="debug-header">
      <button (click)="toggleMobileDebug()" class="debug-toggle">
        {{ showMobileDebug ? 'Hide' : 'Show' }} Debug Console
      </button>
      <button *ngIf="showMobileDebug" (click)="clearMobileDebugLogs()" class="debug-clear">
        Clear
      </button>
    </div>
    <div *ngIf="showMobileDebug" class="debug-content">
      <div class="debug-log" *ngFor="let log of mobileDebugLogs">
        {{ log }}
      </div>
    </div>
  </div><!-- Fullscreen Control Bar - Minimized State -->
  <div *ngIf="(isFullWindowMode || isNativeFullscreen) && miniControlsMinimized"
       class="fullscreen-control-bar minimized"
       [class.position-top-left]="fullscreenControlsPosition === 'top-left'"
       [class.position-top-right]="fullscreenControlsPosition === 'top-right'"
       [class.position-bottom-left]="fullscreenControlsPosition === 'bottom-left'"
       [class.position-bottom-right]="fullscreenControlsPosition === 'bottom-right'">
    <button
      (click)="toggleMiniControlsMinimized()"
      class="mini-restore-btn"
      title="Show Controls"
      type="button"
    >
      ✕
    </button>
  </div>
  <!-- Fullscreen Control Bar (windowed or native) -->
  <div *ngIf="(isFullWindowMode || isNativeFullscreen) && !miniControlsMinimized"
       class="fullscreen-control-bar"
       [class.position-top-left]="fullscreenControlsPosition === 'top-left'"
       [class.position-top-right]="fullscreenControlsPosition === 'top-right'"
       [class.position-bottom-left]="fullscreenControlsPosition === 'bottom-left'"
       [class.position-bottom-right]="fullscreenControlsPosition === 'bottom-right'">
    <button
      (click)="toggleMiniControlsMinimized()"
      class="fullscreen-bar-btn minimize-btn"
      title="Minimize Controls"
      type="button"
    >
      ▾
    </button>
    <button
      (click)="isFullWindowMode ? exitFullWindow() : exitNativeFullscreen()"
      (touchstart)="onCloseButtonTouch($event)"
      class="close-btn"
      title="Exit Fullscreen (Escape)"
      type="button"
    >
      ✖️
    </button>
    <button
      (click)="togglePlayPause()"
      class="fullscreen-bar-btn"
      title="Play/Pause (Space/K)"
      type="button"
    >
      <span *ngIf="!isPlaying">▶️</span>
      <span *ngIf="isPlaying">⏸️</span>
    </button>
    <button
      (click)="toggleHtml5Controls()"
      class="fullscreen-bar-btn"
      [class.active]="showHtml5Controls"
      title="{{ showHtml5Controls ? 'Hide Controls (C)' : 'Show Controls (C)' }}"
      type="button"
    >
      <span *ngIf="showHtml5Controls">🕹️</span><span *ngIf="!showHtml5Controls">🕹️🚫</span>
    </button>
    <button
      (click)="toggleLoopCurrent()"
      class="fullscreen-bar-btn"
      [class.active]="isLoopingCurrent"
      title="Toggle Loop Current Media (L)"
      type="button"
    >
      <span *ngIf="isLoopingCurrent">🔄</span><span *ngIf="!isLoopingCurrent">🔁</span>
    </button>
    <button
      (click)="toggleMute()"
      class="fullscreen-bar-btn"
      [class.active]="isMuted"
      title="Mute/Unmute (M)"
      type="button"
    >
      <span *ngIf="isMuted">🔇</span><span *ngIf="!isMuted">🔊</span>
    </button>
  </div>

  <!-- Video Player -->
  <div class="video-wrapper" [class.has-video]="currentVideo">    
    <!-- Video Element -->
    <video
      #videoElement
      *ngIf="currentVideo && currentVideo.contentType === 'video'"
      class="video-element"
      [src]="currentVideo.url"
      [poster]="getPosterUrl()"
      [loop]="shouldLoop"
      [attr.controls]="showHtml5Controls ? '' : null"
      (loadeddata)="onVideoLoaded()"
      (timeupdate)="onVideoTimeUpdate()"
      (ended)="onVideoEnded()"
      (error)="onVideoError($event)"
      (play)="onVideoPlay()"
      (pause)="onVideoPause()"
      (volumechange)="onVolumeChange()"
      (click)="onVideoClick()"
      preload="auto"
      playsinline
    >
      Your browser does not support the video tag.
    </video>

    <!-- Image Element -->
    <img
      #imageElement
      *ngIf="currentVideo && currentVideo.contentType === 'image'"
      class="image-element"
      [class.pinch-zoom-enabled]="isPinchZoomEnabled"
      [src]="currentVideo.url"
      [alt]="'Image ' + currentVideo.id"
      [style.transform]="imageTransform"
      (load)="onImageLoaded()"
      (error)="onImageError($event)"
      (click)="onImageClick()"
      (keyup.enter)="onImageClick()"
      (keyup.space)="onImageClick()"
      tabindex="0"
      role="button"
      [attr.aria-label]="'Image ' + currentVideo.id + ' - Press Enter or Space to interact'"
    />

    <!-- Placeholder when no video -->
    <div *ngIf="!currentVideo" class="video-placeholder">
      <div class="placeholder-content">
        <div class="placeholder-icon">🎬</div>
        <h3>No Media Selected</h3>
        <p>Search to start the show</p>
      </div>
    </div>
  </div>

  <!-- Window-edge playback progress (fullscreen, controls hidden) -->
  <div
    *ngIf="showEdgeProgressBar"
    class="edge-progress-bar"
    role="progressbar"
    aria-label="Playback progress"
    aria-valuemin="0"
    aria-valuemax="100"
    [attr.aria-valuenow]="mediaProgressPercent"
  >
    <div
      class="edge-progress-fill"
      [style.width.%]="mediaProgressPercent"
      [style.transitionDuration]="mediaProgressTransition"
    ></div>
  </div>

  <!-- Controls -->  <div *ngIf="currentVideo && !isFullWindowMode && !isNativeFullscreen && showControls" class="video-controls">
    <div class="controls-top">
      <div class="control-buttons">
        <!-- Navigation Back Controls Group -->
        <div class="button-group navigation-back-group">
          <button
            (click)="resetToFirst()"
            class="control-btn"
            title="Reset to First Item (H)"
            [disabled]="position.current <= 1"
          >
            ⏪
          </button>

          <button
            (click)="goBack10()"
            class="control-btn"
            title="Back 10 Items (Page Up)"
            [disabled]="position.current <= 10"
          >
            ⏮️10
          </button>

          <button
            (click)="previousVideo()"
            class="control-btn"
            title="Previous Item (←/A)"
            [disabled]="position.current <= 1"
          >
            ⏮️
          </button>
        </div>

        <!-- Vertical Separator -->
        <div class="control-separator"></div>

        <!-- Playback Controls Group -->
        <div class="button-group playback-group">
          <button
            (click)="togglePlayPause()"
            class="control-btn play-pause"
            title="Play/Pause (Space/K)"
          >
            <span *ngIf="!isPlaying">▶️</span>
            <span *ngIf="isPlaying">⏸️</span>
          </button>

          <button
            (click)="toggleLoopCurrent()"
            class="control-btn loop-btn"
            title="Loop Current Media (L)"
            [class.active]="isLoopingCurrent"
          >
            🔄
          </button>
        </div>

        <!-- Vertical Separator -->
        <div class="control-separator"></div>

        <!-- Navigation Forward Controls Group -->
        <div class="button-group navigation-forward-group">
          <button (click)="nextVideo()" class="control-btn" title="Next Item (→/D)">
            ⏭️
          </button>

          <button
            (click)="goForward10()"
            class="control-btn"
            title="Forward 10 Items (Page Down)"
          >
            ⏭️10
          </button>
        </div>

        <!-- Vertical Separator -->
        <div class="control-separator"></div>        <!-- Action Controls Group -->
        <div class="button-group action-group">
          <!-- Favorite Button with Vote Count -->
          <div class="favorite-with-score" *ngIf="favoritesEnabled">
            <app-favorite-button
              [isFavorited]="isFavorited"
              [isLoading]="isFavoriteLoading"
              [title]="isFavorited ? 'Remove from Favorites' : 'Add to Favorites'"
              (favoriteToggle)="toggleFavorite()"
            ></app-favorite-button>
            <span class="vote-count" *ngIf="currentVideo?.score"
              [class.positive]="currentVideo!.score.total > 0"
              [class.negative]="currentVideo!.score.total < 0"
              title="Score: ↑{{ currentVideo!.score.up }} ↓{{ currentVideo!.score.down }}">
              {{ currentVideo!.score.total }}
            </span>
          </div>

          <!-- Mute Toggle Button -->
          <button
            (click)="toggleMute()"
            class="control-btn mute-btn"
            title="Mute/Unmute (M)"
            [class.active]="isMuted"
          >
            <span *ngIf="isMuted">🔇</span>
            <span *ngIf="!isMuted">🔊</span>
          </button>

          <!-- Hide HTML5 Controls Button -->
          <button
            (click)="toggleHtml5Controls()"
            class="control-btn html5-controls-btn"
            title="Hide/Show HTML5 Video Controls"
            [class.active]="!showHtml5Controls"
          >
            <span *ngIf="showHtml5Controls">🎮</span>
            <span *ngIf="!showHtml5Controls">🎮🚫</span>
          </button>

          <!-- Fullscreen Mode Buttons -->
          <button
            (click)="toggleFullWindowMode()"
            class="control-btn fullscreen-btn"
            title="Full Window Mode - Media fills window with controls (W)"
            [class.active]="isFullWindowMode && !isNativeFullscreen"
          >
            🖼️
          </button>

          <button
            (click)="toggleNativeFullscreen()"
            class="control-btn fullscreen-btn"
            title="Native Fullscreen - Media fills entire screen (F)"
            [class.active]="isNativeFullscreen"
          >
            ⛶
          </button>
        </div>
      </div>
      <div class="position-info">{{ getPositionInfo() }}</div>
      <!-- Date Period Navigation -->
      <div class="date-nav-row" *ngIf="hasDateNavigation">
        <button type="button" class="date-nav-btn" (click)="shiftDateEarlier()"
          title="Shift earlier  [">◀ Earlier</button>
        <span class="date-nav-label">{{ activeDateLabel }}</span>
        <button type="button" class="date-nav-btn" (click)="shiftDateLater()"
          [disabled]="!canShiftDateLater"
          title="Shift later  ]">Later ▶</button>
      </div>
      <!-- Keyboard Shortcuts Help - Compact -->
      <div class="keyboard-shortcuts-compact">
        <details>
          <summary>⌨️ Shortcuts</summary>
          <div class="shortcuts-content">            <!-- Playback Controls -->
            <div class="shortcuts-category">
              <h5>🎮 Playback</h5>
              <div class="shortcuts-row">
                <span><kbd>Space</kbd>/<kbd>K</kbd> Play/Pause</span>
                <span><kbd>R</kbd> Restart Media</span>
                <span><kbd>M</kbd> Mute/Unmute</span>
              </div>
              <div class="shortcuts-row">
                <span><kbd>↑</kbd>/<kbd>↓</kbd> Volume ±10%</span>
                <span><kbd>L</kbd> Loop Current</span>
              </div>
            </div>

            <!-- Navigation Controls -->
            <div class="shortcuts-category">
              <h5>🔄 Navigation</h5>
              <div class="shortcuts-row">
                <span><kbd>←</kbd>/<kbd>A</kbd> Previous</span>
                <span><kbd>→</kbd>/<kbd>D</kbd> Next</span>
                <span><kbd>Home</kbd>/<kbd>H</kbd> Reset to First</span>
              </div>
              <div class="shortcuts-row">
                <span><kbd>Page Up</kbd> Back 10</span>
                <span><kbd>Page Down</kbd> Forward 10</span>
              </div>
            </div>

            <!-- Video Seek Controls -->
            <div class="shortcuts-category">
              <h5>⏯️ Video Seek</h5>
              <div class="shortcuts-row">
                <span><kbd>,</kbd>/<kbd>.</kbd> Seek ±5s</span>
                <span><kbd>0-9</kbd> Seek to %</span>
              </div>
            </div>            <!-- Fullscreen Controls -->
            <div class="shortcuts-category">
              <h5>🖥️ Fullscreen</h5>
              <div class="shortcuts-row">
                <span><kbd>W</kbd> Full Window</span>
                <span><kbd>F</kbd> Native Fullscreen</span>
                <span><kbd>Esc</kbd> Exit Fullscreen</span>
              </div>
            </div>            <!-- Actions -->
            <div class="shortcuts-category">
              <h5>⚡ Actions</h5>
              <div class="shortcuts-row">
                <span><kbd>E</kbd> Open on e621</span>
                <span *ngIf="favoritesEnabled"><kbd>S</kbd> Favorite</span>
              </div>
              <div class="shortcuts-row">
                <span><kbd>C</kbd> Hide/Show Controls</span>
              </div>
            </div>

            <!-- Special Navigation -->
            <div class="shortcuts-note">
              <div class="note-title">📱 Touch/Mouse Navigation</div>
              <div class="note-content">
                <strong>All views:</strong> Swipe left=next • Swipe right=previous<br>
                <strong>Fullscreen modes:</strong> Scroll down=next • Scroll up=previous • Swipe up/down on mobile
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>

    <!-- <div class="video-info">
      <div class="position-info">{{ getPositionInfo() }}</div>
      <div class="video-details">{{ getVideoInfo() }}</div>
    </div> -->
    </div>
  <!-- Media Information Panel -->
  <div *ngIf="currentVideo && !isFullWindowMode && !isNativeFullscreen && showControls" class="video-info-container">
    <div class="video-info-panel">
      <details>
        <summary>📋 Media Information</summary>
        <div class="info-content">
        <!-- Basic Info -->
        <div class="info-section">
          <h4>Basic Information</h4>
          <div class="info-grid">
            <div class="info-item">
              <strong>🆔 ID:</strong> {{ currentVideo.id }}
            </div>
            <div class="info-item">
              <strong>🔞 Rating:</strong> <span [ngClass]="{
                'rating-s': currentVideo.rating === 's',
                'rating-q': currentVideo.rating === 'q',
                'rating-e': currentVideo.rating === 'e'
              }">{{ currentVideo.rating.toUpperCase() }}</span>
            </div>
            <div class="info-item">
              <strong>⏱️ Duration:</strong> {{ formatDuration(currentVideo.duration) }}
            </div>
            <div class="info-item">
              <strong>📐 Resolution:</strong> {{ currentVideo.width }}×{{ currentVideo.height }}
            </div>
            <div class="info-item">
              <strong>💾 File Size:</strong> {{ formatFileSize(currentVideo.fileSize) }}
            </div>
            <div class="info-item">
              <strong>📁 Format:</strong> {{ currentVideo.fileExt.toUpperCase() }}
            </div>
            <div class="info-item">
              <strong>📅 Posted:</strong> {{ formatDate(currentVideo.createdAt) }}
            </div>
            <div class="info-item" *ngIf="favoritesEnabled">
              <strong>❤️ Favorited:</strong>
              <span [class.favorited]="isFavorited">
                {{ isFavorited ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="info-item" *ngIf="currentVideo.favCount !== undefined">
              <strong>⭐ Favorites:</strong> <span class="count-badge">{{ currentVideo.favCount }}</span>
            </div>
            <div class="info-item" *ngIf="currentVideo.score && currentVideo.score.up !== undefined && currentVideo.score.down !== undefined">
              <strong>👍 Likes:</strong> <span class="count-badge like">{{ currentVideo.score.up }}</span>
              <strong style="margin-left:8px;">👎 Dislikes:</strong> <span class="count-badge dislike">{{ currentVideo.score.down }}</span>
            </div>
          </div>
        </div>

        <!-- Links -->
        <div class="info-section">
          <h4>Links</h4>
          <div class="links-grid">
            <a
              [href]="'https://e621.net/posts/' + currentVideo.id"
              target="_blank"
              class="action-link e621-link"
              title="View on e621.net"
            >
              🔗 View on e621
            </a>            <button
              (click)="downloadVideo()"
              class="action-link download-link"
              title="Download file"
            >
              💾 Download File
            </button>
          </div>
        </div>        <!-- Description -->
        <div
          class="info-section"
          *ngIf="currentVideo.description && currentVideo.description.trim()"
        >
          <h4>Description</h4>
          <div class="description" [innerHTML]="getFormattedDescription()"></div>
        </div>

        <!-- Sources -->
        <div
          class="info-section"
          *ngIf="currentVideo.sources && currentVideo.sources.length > 0"
        >
          <h4>Sources</h4>
          <div class="sources-list">
            <a
              *ngFor="let source of currentVideo.sources"
              [href]="source"
              target="_blank"
              class="source-link"
            >
              {{ source }}
            </a>
          </div>
        </div>

        <!-- Tags Component -->
        <div class="info-section">
          <app-tags
            [video]="currentVideo"
            (tagClick)="onTagClick($event)">
          </app-tags>
        </div>
      </div>
    </details>
    </div>
  </div>
</div>
